import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  useOrders,
  useStartUnwinder,
  useStopUnwinder,
  useUnwinderState,
  useUnwindingRules
} from '../../api/useUnwinding';
import { useUpdates } from 'src/Modules/Arbitrage/context/updatesContext';

const OPEN_ORDER_STATUSES = new Set(['resting', 'live', 'partial', 'pending']);

export const StopStartUnwinderButtons = () => {
  const { data: state } = useUnwinderState();
  const { data: rules } = useUnwindingRules();
  const { data: ordersData } = useOrders();
  const stopMutation = useStopUnwinder();
  const startMutation = useStartUnwinder();

  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);

  const activeRuleIds = useMemo(
    () =>
      new Set(
        Array.isArray(rules)
          ? rules.filter(r => r.status === 'active').map(r => r._id)
          : []
      ),
    [rules]
  );
  const activeRuleCount = activeRuleIds.size;
  const pausedRuleCount = useMemo(
    () =>
      Array.isArray(rules) ? rules.filter(r => r.status === 'paused').length : 0,
    [rules]
  );
  const { openOrdersFromActiveRules, openOrdersFromPausedRules } = useMemo(() => {
    const openOrders = (ordersData?.orders ?? []).filter(o =>
      OPEN_ORDER_STATUSES.has(o.status)
    );
    return {
      openOrdersFromActiveRules: openOrders.filter(
        o => o.ruleId && activeRuleIds.has(o.ruleId)
      ).length,
      openOrdersFromPausedRules: openOrders.filter(
        o => !o.ruleId || !activeRuleIds.has(o.ruleId)
      ).length,
    };
  }, [ordersData, activeRuleIds]);

  const isRunning = state?.isRunning ?? true;
  const isBusy = stopMutation.isLoading || startMutation.isLoading;
  // Panic only fires when active rules exist. Open orders tied to paused rules
  // are intentionally left alone (user-managed), so they don't block the stop.
  const willTriggerPanic = activeRuleCount > 0;

  // Shared system/job stepper — only show entries from the unwinder subsystem
  const { currentJob } = useUpdates();
  const isMyJob = Boolean(
    currentJob &&
      currentJob.subsystem === 'unwinder' &&
      (currentJob.status === 'running' ||
        (currentJob.completedAt && Date.now() - currentJob.completedAt < 5000))
  );

  // Keep the stepper visible for ~4s after completion, then hide
  const [showJobResult, setShowJobResult] = useState(false);
  const prevJobRunning = useRef(false);
  useEffect(() => {
    if (isMyJob) {
      prevJobRunning.current = true;
      setShowJobResult(true);
    } else if (prevJobRunning.current && !isMyJob) {
      prevJobRunning.current = false;
      const timer = setTimeout(() => setShowJobResult(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isMyJob]);

  const jobProgress = useMemo(() => {
    if (!currentJob || !isMyJob) return 0;
    const completed = currentJob.steps.filter(s => s.status === 'completed').length;
    return Math.round((completed / currentJob.steps.length) * 100);
  }, [currentJob, isMyJob]);
  const showProgress = (isMyJob || showJobResult) && currentJob;

  const handleConfirmStop = () => {
    stopMutation.mutate();
    setStopConfirmOpen(false);
  };

  const handleConfirmStart = () => {
    startMutation.mutate();
    setStartConfirmOpen(false);
  };

  const stopRunning =
    isMyJob && currentJob?.type === 'stop' && currentJob?.status === 'running';
  const startRunning =
    isMyJob && currentJob?.type === 'start' && currentJob?.status === 'running';

  // Compact status chip + transient result snackbar so the operator always has
  // an at-a-glance signal even if the stepper popover lags or auto-dismisses.
  // The stepper depends on the system-job poll loop (every 2s) seeded by the
  // mutation; `useUnwinderState` polls /unwinder/state every 3s independently
  // and is the source of truth for "did the pod actually scale down".
  const status: 'running' | 'stopping' | 'starting' | 'stopped' = stopRunning
    ? 'stopping'
    : startRunning
      ? 'starting'
      : isRunning
        ? 'running'
        : 'stopped';

  const statusLabel = {
    running: 'Running',
    stopping: 'Stopping…',
    starting: 'Starting…',
    stopped: 'Stopped'
  }[status];

  const statusColor: 'success' | 'warning' | 'info' | 'default' = {
    running: 'success',
    stopping: 'warning',
    starting: 'info',
    stopped: 'default'
  }[status] as 'success' | 'warning' | 'info' | 'default';

  const [resultSnack, setResultSnack] = useState<
    { kind: 'success' | 'error'; message: string } | null
  >(null);

  // Watch for job completion AFTER it was mine. The popover already shows
  // step-by-step progress; this is the "did it actually finish" toast that
  // survives even if the popover auto-dismisses or the user has navigated.
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (
      currentJob &&
      currentJob.subsystem === 'unwinder' &&
      prev === 'running' &&
      (currentJob.status === 'completed' || currentJob.status === 'failed')
    ) {
      const verb = currentJob.type === 'stop' ? 'stop' : 'start';
      if (currentJob.status === 'completed') {
        setResultSnack({
          kind: 'success',
          message: `Unwinder ${verb} completed${
            currentJob.triggeredPanic ? ' (panic was triggered)' : ''
          }.`
        });
      } else {
        setResultSnack({
          kind: 'error',
          message: `Unwinder ${verb} failed: ${currentJob.error ?? 'unknown error'}`
        });
      }
    }
    prevStatusRef.current = currentJob?.status;
  }, [currentJob]);

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip
            title={
              status === 'stopping'
                ? 'Unwinder is stopping — pod is being scaled to 0'
                : status === 'starting'
                  ? 'Unwinder is starting — waiting for pod Ready'
                  : status === 'running'
                    ? `Unwinder pod running${
                        typeof state?.replicas === 'number'
                          ? ` (${state.replicas} replica${state.replicas === 1 ? '' : 's'})`
                          : ''
                      }`
                    : `Unwinder stopped${
                        state?.lastStopAt
                          ? ` ${new Date(state.lastStopAt).toLocaleTimeString()}`
                          : ''
                      }`
            }
            placement="top"
          >
            <Chip
              size="small"
              label={statusLabel}
              color={statusColor}
              variant={status === 'stopped' ? 'outlined' : 'filled'}
              icon={
                status === 'stopping' || status === 'starting' ? (
                  <CircularProgress size={10} color="inherit" sx={{ ml: 0.5 }} />
                ) : undefined
              }
              sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
            />
          </Tooltip>
          <Tooltip
            title={
              stopRunning
                ? 'Stopping unwinder…'
                : !isRunning
                  ? 'Unwinder already stopped'
                  : 'Stop position-unwinder instance'
            }
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                  stopMutation.isLoading || stopRunning ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PowerSettingsNewIcon />
                  )
                }
                onClick={() => setStopConfirmOpen(true)}
                disabled={isBusy || !isRunning || Boolean(isMyJob && currentJob?.status === 'running')}
                sx={{ fontSize: '0.7rem' }}
              >
                {stopRunning ? 'Stopping' : 'Stop Unwinder'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              startRunning
                ? 'Starting unwinder…'
                : isRunning
                  ? 'Unwinder already running'
                  : 'Start position-unwinder instance'
            }
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={
                  startMutation.isLoading || startRunning ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() => setStartConfirmOpen(true)}
                disabled={isBusy || isRunning || Boolean(isMyJob && currentJob?.status === 'running')}
                sx={{ fontSize: '0.7rem' }}
              >
                {startRunning ? 'Starting' : 'Start Unwinder'}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {/* Progress popover */}
        {showProgress && currentJob && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: 0,
              mt: 1,
              zIndex: 1400
            }}
          >
            <Paper elevation={6} sx={{ p: 1.5, minWidth: 280, maxWidth: 340, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {currentJob.type === 'start' ? 'Starting' : 'Stopping'} Unwinder
                </Typography>
                {currentJob.status === 'completed' && (
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                )}
                {currentJob.status === 'failed' && (
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                )}
              </Box>

              {currentJob.status === 'running' && (
                <LinearProgress
                  variant="determinate"
                  value={jobProgress}
                  sx={{ mb: 1, borderRadius: 1, height: 3 }}
                />
              )}

              <Stepper
                activeStep={currentJob.currentStep}
                orientation="vertical"
                sx={{
                  '& .MuiStepConnector-line': { minHeight: 8 },
                  '& .MuiStepLabel-label': { fontSize: '0.7rem' },
                  '& .MuiStepLabel-iconContainer': { pr: 1 },
                  '& .MuiSvgIcon-root': { fontSize: '0.9rem' }
                }}
              >
                {currentJob.steps.map((step, idx) => (
                  <Step key={idx} completed={step.status === 'completed'}>
                    <StepLabel error={step.status === 'failed'} sx={{ py: 0 }}>
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {currentJob.error && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {currentJob.error}
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </Box>

      {/* Stop confirmation */}
      <Dialog
        open={stopConfirmOpen}
        onClose={() => setStopConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 0.5 }}>Stop Position Unwinder</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'grey.400' }}>
              This scales the <code>position-unwinder</code> deployment to 0 replicas.
              It is separate from the global Predictions on/off and from PANIC.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Rules to pause:</b> {activeRuleCount}
                {pausedRuleCount > 0 && (
                  <Typography component="span" sx={{ fontSize: '0.8rem', color: 'grey.500' }}>
                    {' '}({pausedRuleCount} already paused)
                  </Typography>
                )}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Orders to cancel:</b> {openOrdersFromActiveRules}
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                  {' '}(from active rules)
                </Typography>
              </Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Orders to remain open:</b> {openOrdersFromPausedRules}
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                  {' '}(unrelated to active rules)
                </Typography>
              </Typography>
            </Box>
            {willTriggerPanic ? (
              <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                <b>PANIC</b> will fire to pause {activeRuleCount} rule
                {activeRuleCount === 1 ? '' : 's'} and cancel {openOrdersFromActiveRules} order
                {openOrdersFromActiveRules === 1 ? '' : 's'}, then the unwinder will stop.
                {openOrdersFromPausedRules > 0 && (
                  <> {openOrdersFromPausedRules} order{openOrdersFromPausedRules === 1 ? '' : 's'} not tied to active rules will remain open.</>
                )}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                No active rules. Panic will be skipped; unwinder will stop directly.
                {openOrdersFromPausedRules > 0 && (
                  <> {openOrdersFromPausedRules} open order{openOrdersFromPausedRules === 1 ? '' : 's'} not tied to active rules will remain open.</>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopConfirmOpen(false)} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStop}
            variant="contained"
            color="warning"
            size="small"
            disabled={stopMutation.isLoading}
          >
            {stopMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Stop Unwinder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start confirmation */}
      <Dialog
        open={startConfirmOpen}
        onClose={() => setStartConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 0.5 }}>Start Position Unwinder</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.8rem', color: 'grey.400' }}>
            Scale <code>position-unwinder</code> back to 1 replica.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartConfirmOpen(false)} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStart}
            variant="contained"
            color="success"
            size="small"
            disabled={startMutation.isLoading}
          >
            {startMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Start Unwinder'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(resultSnack)}
        autoHideDuration={6000}
        onClose={() => setResultSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {resultSnack ? (
          <Alert
            severity={resultSnack.kind}
            onClose={() => setResultSnack(null)}
            sx={{ fontSize: '0.8rem' }}
          >
            {resultSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};
